import React, { useState, useEffect } from 'react';
import { FaTimes, FaExternalLinkAlt } from 'react-icons/fa';

const ProjectLinksModal = ({ isOpen, onClose, apiUrl, apiFetch, fetchUrl }) => {
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (isOpen) {
            fetchLinks();
        } else {
            setLinks([]);
            setSearchTerm('');
            setError(null);
        }
    }, [isOpen]);

    const fetchLinks = async () => {
        setLoading(true);
        setError(null);
        try {
            // Use custom fetchUrl if provided (for admin), otherwise build client URL
            let url;
            if (fetchUrl) {
                url = fetchUrl;
            } else {
                const leadId = localStorage.getItem('clientLeadId');
                const timestamp = localStorage.getItem('clientTimestamp');
                url = `${apiUrl}/client/project-links?leadId=${encodeURIComponent(leadId)}&timestamp=${encodeURIComponent(timestamp)}`;
            }

            const response = await apiFetch(url);

            if (!response.ok) {
                throw new Error('Failed to fetch project links');
            }

            const data = await response.json();
            setLinks(data.links || []);
        } catch (err) {
            console.error('Error fetching project links:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const filteredLinks = links.filter(link => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            (link.description && link.description.toLowerCase().includes(search)) ||
            (link.url && link.url.toLowerCase().includes(search))
        );
    });

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[80vh] flex flex-col">
                {/* Header with Search */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">
                        Global Project Links
                        {!loading && <span className="text-sm font-normal text-gray-500 ml-2">({filteredLinks.length})</span>}
                    </h2>
                    <div className="flex items-center gap-3">
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 w-80"
                        />
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 p-1"
                        >
                            <FaTimes size={18} />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-16">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                            <span className="ml-3 text-sm text-gray-500">Loading links...</span>
                        </div>
                    ) : error ? (
                        <div className="text-center py-16">
                            <p className="text-red-600 text-sm">{error}</p>
                            <button
                                onClick={fetchLinks}
                                className="mt-3 px-4 py-1.5 text-sm bg-primary-600 text-white rounded hover:bg-primary-700"
                            >
                                Retry
                            </button>
                        </div>
                    ) : filteredLinks.length === 0 ? (
                        <div className="text-center py-16 text-sm text-gray-500">
                            {searchTerm ? 'No links match your search.' : 'No links found in this project.'}
                        </div>
                    ) : (
                        <table className="w-full table-fixed">
                            <thead className="bg-gray-50 sticky top-0 border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-[20%]">
                                        Description
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-[35%]">
                                        URL
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-[35%]">
                                        Task
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-[10%]">
                                        Type
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredLinks.map((link, index) => (
                                    <tr
                                        key={`${link.taskId}-${link.linkNumber}-${index}`}
                                        className={`${index % 2 === 0 ? 'bg-white' : 'bg-slate-100'} hover:bg-yellow-50 transition-colors`}
                                    >
                                        <td className="px-6 py-3 text-sm" style={{ color: '#374151' }}>
                                            {link.description || <span className="text-gray-400">-</span>}
                                        </td>
                                        <td className="px-6 py-3 text-sm text-gray-900">
                                            {link.url ? (
                                                <a
                                                    href={link.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-primary-600 hover:text-primary-800 hover:underline inline-flex items-center gap-1"
                                                >
                                                    <span className="truncate block" title={link.url}>
                                                        {link.url}
                                                    </span>
                                                    <FaExternalLinkAlt className="flex-shrink-0 opacity-50" size={10} />
                                                </a>
                                            ) : (
                                                <span className="text-gray-400">-</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-3 text-sm">
                                            <button
                                                onClick={() => {
                                                    window.dispatchEvent(new CustomEvent('openTaskFromLinks', { detail: { taskId: link.taskId } }));
                                                }}
                                                className="text-primary-600 hover:text-primary-800 hover:underline truncate block text-left"
                                                title={link.taskName}
                                            >
                                                {link.taskName}
                                            </button>
                                        </td>
                                        <td className="px-6 py-3 text-sm text-gray-600">
                                            {link.isSubtask ? 'Sub Task' : 'Main Task'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProjectLinksModal;
